import { t } from 'i18next';
import { Control, UseFormGetValues, UseFormHandleSubmit, UseFormSetValue } from 'react-hook-form';

import { Button, Confirmation, DateInput, Modal, Visible } from 'coreOld/components';
import { Grid } from 'coreOld/elements';
import { AlignText, ButtonType, Size } from 'coreOld/enums';
import { HeaderItem, HeaderWrapper } from 'partsOld/elements';
import { IPricing, IPricingItem, IPricingForm } from 'partsOld/types';

import {
  BottomBtnWrapper,
  BtnWrapper,
  ButtonWrapper,
  FieldsWrapper,
  List,
  ListWrapper,
  RemoveBtn,
  Wrapper,
} from './pricingFormStyle';
import PricingItem from './PricingItem';

const getConfirmationModalLabels = (isRemoveMode: boolean) => {
  if (isRemoveMode) {
    return {
      confirmationTitle: t('labelRemoveActivePartPrice'),
      confirmBtnLabel: t('buttonLabelRemove'),
      message: t('messageRemoveActivePartPrice'),
    };
  }

  return {
    confirmationTitle: t('labelEndActivePartPrice'),
    confirmBtnLabel: t('buttonLabelEnd'),
    message: t('messageEndActivePartPrice'),
  };
};

interface IProps {
  control: Control<IPricingForm, object>;
  startDate: Date;
  endDate: Date;
  isFormOpen: boolean;
  priceItems?: IPricingItem[];
  isActivePriceOverlapping?: boolean;
  removedItem: IPricing;
  isPriceSelected: boolean;
  handleSubmit: UseFormHandleSubmit<IPricingForm>;
  getValues: UseFormGetValues<IPricingForm>;
  setValue: UseFormSetValue<IPricingForm>;
  addPrice?: (skipValidation: boolean) => Promise<void>;
  removePrice?: (index: number, item?: IPricingItem) => void;
  onChangeDate: (date, field: string) => void;
  onSubmit: (skipActiveCheck?: boolean) => (data: any) => Promise<void>;
  onCloseModal: () => void;
  onCancel: () => void;
  onCancelBtnClick: () => void;
  onRemove: () => void;
  onCancelRemove: () => void;
}

const headerItems = [
  {
    label: 'Min Qty',
    gridItem: 2.8,
    style: {
      textAlign: AlignText.RIGHT,
    },
  },
  {
    label: 'Max Qty',
    gridItem: 2.8,
    style: {
      textAlign: AlignText.RIGHT,
    },
  },
  {
    label: 'Margin Reduction',
    gridItem: 3,
    style: {
      textAlign: AlignText.RIGHT,
    },
  },
  {
    label: 'Discount Increase',
    gridItem: 2.5,
    style: {
      textAlign: AlignText.RIGHT,
    },
  },
  {
    label: '',
    gridItem: 0.5,
  },
];

const PricingFormView = ({
  startDate,
  endDate,
  onChangeDate,
  addPrice,
  removePrice,
  priceItems,
  control,
  isFormOpen,
  onCloseModal,
  onSubmit,
  handleSubmit,
  getValues,
  isPriceSelected,
  removedItem,
  onCancel,
  onCancelBtnClick,
  onCancelRemove,
  onRemove,
  setValue,
  isActivePriceOverlapping,
}: IProps) => {
  const title = isPriceSelected ? t('labelUpdatePartPrice') : t('labelCreatePartPrice');
  const isRemoveModal = Boolean(removedItem);
  const labels = getConfirmationModalLabels(isRemoveModal);

  return (
    <>
      <Modal title={title} isOpen={isFormOpen} onClose={onCloseModal} size={Size.LG}>
        <Wrapper>
          <FieldsWrapper container spacing={1}>
            <Grid item xs={12} sm={6}>
              <DateInput
                label={t('labelStartDate')}
                selected={startDate}
                startDate={startDate}
                minDate={new Date()}
                onChange={(date) => onChangeDate(date, 'startDate')}
                showTimeInput={true}
                size={Size.LG}
                showBorder={true}
                hasBorder={false}
                required
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <DateInput
                label={t('labelEndDate')}
                selected={endDate}
                endDate={endDate}
                startDate={startDate}
                minDate={startDate}
                onChange={(date) => onChangeDate(date, 'endDate')}
                showTimeInput={true}
                size={Size.LG}
                showBorder={true}
                hasBorder={false}
              />
            </Grid>
          </FieldsWrapper>

          <ListWrapper>
            <List>
              <HeaderWrapper container spacing={1}>
                {headerItems.map((item) => (
                  <HeaderItem
                    item
                    xs={item.gridItem as any}
                    key={item.label}
                    style={{ ...item.style }}
                  >
                    {item.label}
                  </HeaderItem>
                ))}
              </HeaderWrapper>

              {priceItems.map((item, index) => {
                return (
                  <PricingItem
                    key={item.id}
                    control={control}
                    index={index}
                    getValues={getValues}
                    setValue={setValue}
                    removePrice={() => removePrice(index, item)}
                  />
                );
              })}
            </List>
          </ListWrapper>

          <BtnWrapper>
            <ButtonWrapper>
              <Button onClick={() => addPrice(false)} size={Size.SM}>
                {t('buttonLabelAddQuantity')}
              </Button>
            </ButtonWrapper>

            <Button onClick={handleSubmit(onSubmit(false))}>{title}</Button>
          </BtnWrapper>

          <Visible when={isPriceSelected}>
            <BottomBtnWrapper>
              <RemoveBtn>
                <Button onClick={onCancelBtnClick} btnType={ButtonType.DANGER}>
                  {t('buttonLabelRemoveActivePartPrice')}
                </Button>
              </RemoveBtn>
            </BottomBtnWrapper>
          </Visible>
        </Wrapper>
      </Modal>

      <Visible when={isActivePriceOverlapping || isRemoveModal}>
        <Confirmation
          isOpen={isActivePriceOverlapping || isRemoveModal}
          onConfirm={isRemoveModal ? onRemove : handleSubmit(onSubmit(true))}
          onCancel={isRemoveModal ? onCancelRemove : onCancel}
          title={labels.confirmationTitle}
          confirmBtnLabel={labels.confirmBtnLabel}
          message={labels.message}
          confirmBtnType={ButtonType.DANGER}
          size={Size.MD}
        />
      </Visible>
    </>
  );
};

export default PricingFormView;
